import React, { useEffect, useState } from 'react';
import { Box, HStack, Heading, Icon, Switch, Text, VStack, VisuallyHidden } from '@chakra-ui/react';
import { BadgeCheck } from 'lucide-react';
import { type User, useUpdateProfileAccountStatusMutation } from '../api';
import { isFlagOn } from '../flags';

const SAVE_FAILED = 'Could not save the account status. Try again.';

/** Profile `account_status` switch in the user drawer, saved through the admin profile route. */
export function ProfileAccountStatusSection({ user }: Readonly<{ user: User }>) {
  const [save, { isLoading }] = useUpdateProfileAccountStatusMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Shown while the save and the user refetch are in flight, so the switch doesn't flick back.
  const [requested, setRequested] = useState<boolean | null>(null);
  // The switch's hidden input keeps its own checked state; remount it after a failed save so it
  // matches the stored value again (as ClarityChatbotOptInSection does).
  const [revision, setRevision] = useState(0);

  const profile = user.profile;
  const stored = isFlagOn(profile?.account_status);

  // Drop the requested value once the refetched user shows it.
  useEffect(() => {
    if (requested !== null && stored === requested) setRequested(null);
  }, [stored, requested]);

  if (!profile) return null;
  const checked = requested ?? stored;

  const onCheckedChange = async (value: boolean) => {
    setErrorMessage(null);
    setRequested(value);
    try {
      await save({ userId: user.id, profileId: profile.id, accountStatus: value }).unwrap();
    } catch {
      setErrorMessage(SAVE_FAILED);
      setRequested(null);
      setRevision((value) => value + 1);
    }
  };

  return (
    <Box
      p={4}
      borderWidth={1}
      borderRadius="md"
      borderColor="gray.200"
      bg="white"
      _dark={{ borderColor: 'gray.600', bg: 'gray.700' }}
      data-testid="profile-account-status"
    >
      <VStack align="start" gap={3}>
        <HStack>
          <Icon as={BadgeCheck} boxSize={4} color="brand.500" />
          <Heading size="sm" color="brand.600" _dark={{ color: 'brand.300' }}>Profile account</Heading>
        </HStack>
        <Switch.Root key={revision} checked={checked} disabled={isLoading} onCheckedChange={(details) => onCheckedChange(details.checked)}>
          <Switch.HiddenInput />
          <Switch.Control />
          {/* The input is labelled by this label (aria-labelledby), so the context goes inside it. */}
          <Switch.Label><VisuallyHidden>Profile account </VisuallyHidden>{checked ? 'Active' : 'Inactive'}</Switch.Label>
        </Switch.Root>
        <Text fontSize="xs" color="gray.500">
          Saves immediately. Only doctors whose profile account is active are published to the Clarity chatbot catalog.
        </Text>
        {errorMessage && <Text fontSize="sm" color="red.500" role="alert">{errorMessage}</Text>}
      </VStack>
    </Box>
  );
}
